import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { notifications } from "@mantine/notifications";

export interface AdminRole {
  id: string;
  role_key: string | null;
  name: string;
  description: string | null;
  level: number;
  created_at: string | null;
}

const QUERY_KEY = ["admin", "roles"] as const;

export function useAdminRoles(enabled = true) {
  return useQuery<AdminRole[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<{ data: AdminRole[] }>("/admin/roles");
      return res.data.data;
    },
    enabled,
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; level: number }) =>
      api.post("/admin/roles", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["admin", "permissions"] });
      notifications.show({ message: "Role created", color: "green" });
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to create role" },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      description?: string | null;
      level?: number;
    }) => api.patch(`/admin/roles/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["admin", "permissions"] });
      notifications.show({ message: "Role updated", color: "green" });
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to update role" },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/roles/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      qc.invalidateQueries({ queryKey: ["admin", "permissions"] });
      notifications.show({ message: "Role deleted", color: "green" });
    },
    // US-081: global MutationCache handler fires this fallback; no per-hook
    // onError needed.
    meta: { errorFallback: "Failed to delete role" },
  });
}
